import { useMemo } from 'react';
import { toAccountId } from '@atlassian/jira-shared-types';
import { toIssueId } from '@atlassian/jira-software-deployments-common-table/src/model/issue';
import { toIssueTypeId } from '@atlassian/jira-software-deployments-common-table/src/model/issue-type';
import { DATE_FORMAT_DASH } from '../../common/constants';
import { getDate, formatDate } from '../../common/utils/dates';
import {
	defaultDateFilter,
	type State as DateFilterState,
	type DateFilter,
	useDateFilterStore,
	initialState as dateFilterInitialState,
} from '../date-filter';
import { toRelativeDate, isRelativeDate } from '../date-filter/utils';
import { useTableFilterStore, type State as TopFilter, initialState } from '../top-filter';
import {
	QUERY_PARAM_ASSIGNEE,
	QUERY_PARAM_ENVIRONMENT_NAME,
	QUERY_PARAM_TEXT,
	QUERY_PARAM_ISSUE_TYPE,
	QUERY_PARAM_EPIC,
	QUERY_PARAM_FIX_VERSION,
	QUERY_PARAM_START_DATE,
	QUERY_PARAM_END_DATE,
} from './constants';
import type { QueryParams, SearchQueryParams } from './types';

const DELIMITER = ',';
const ENCODED_DELIMITER = encodeURIComponent(DELIMITER);

// we need to encode the delimiter to protect our free text values from containing
// the same character that delimits the list.
// e.g.
// env names = ["prod,east", "prod,west"] -> "prod,east,prod,west"
const encodeDelimeter = (
	// @ts-expect-error - TS2304 - Cannot find name 'IssueTypeId'. | TS2304 - Cannot find name 'AccountId'. | TS2304 - Cannot find name 'EnvironmentName'. | TS2304 - Cannot find name 'FixVersion'. | TS2304 - Cannot find name 'EpicIdOption'.
	input: IssueTypeId | AccountId | EnvironmentName | FixVersion | EpicIdOption,
) => {
	const regex = new RegExp(DELIMITER, 'g');
	return input.replace(regex, ENCODED_DELIMITER);
};

const decodeDelimeter = (input: string) => {
	const regex = new RegExp(ENCODED_DELIMITER, 'g');
	return input.replace(regex, DELIMITER);
};

// @ts-expect-error - TS7006 - Parameter 'arr' implicitly has an 'any' type.
const filterToQueryParam = (arr) => {
	if (arr.length === 0) return undefined;
	// @ts-expect-error - TS7006 - Parameter 'entity' implicitly has an 'any' type.
	return arr.map((entity) => encodeDelimeter(entity)).join(DELIMITER);
};

const queryParamToFilter = (str: string) => str.split(DELIMITER).map(decodeDelimeter);

export const getDateFilterFromQueryParams = (queryParams: SearchQueryParams): DateFilterState => {
	const dateFilterState: DateFilterState = {
		...dateFilterInitialState,
	};

	if (queryParams[QUERY_PARAM_START_DATE] != null && queryParams[QUERY_PARAM_END_DATE] != null) {
		const startDate = queryParams[QUERY_PARAM_START_DATE];
		const endDate = queryParams[QUERY_PARAM_END_DATE];

		if (isRelativeDate(startDate) && startDate !== 'now' && endDate === 'now') {
			dateFilterState.dateFilter = {
				type: 'RELATIVE',

				startDate: toRelativeDate(startDate) ?? defaultDateFilter.startDate,
				endDate: toRelativeDate(endDate) ?? defaultDateFilter.endDate,
			};
		} else {
			const validatedStartDate = getDate(startDate);

			const validatedEndDate = getDate(endDate);
			if (validatedStartDate instanceof Date && validatedEndDate instanceof Date) {
				dateFilterState.dateFilter = {
					type: 'CUSTOM',
					startDate: validatedStartDate,
					endDate: validatedEndDate,
				};
			} else {
				throw new Error('invalid date filter query param');
			}
		}
	}

	return dateFilterState;
};

export const getFiltersFromQueryParams = (queryParams: SearchQueryParams): TopFilter => {
	const topFilterState: TopFilter = {
		...initialState,
	};

	if (queryParams[QUERY_PARAM_TEXT] != null) {
		topFilterState.text = queryParams[QUERY_PARAM_TEXT];
	}

	if (queryParams[QUERY_PARAM_ASSIGNEE] != null) {
		topFilterState.assignees = queryParamToFilter(queryParams[QUERY_PARAM_ASSIGNEE]).map(
			(accountId) => toAccountId(accountId),
		);
	}

	if (queryParams[QUERY_PARAM_ENVIRONMENT_NAME] != null) {
		topFilterState.environmentNames = queryParamToFilter(queryParams[QUERY_PARAM_ENVIRONMENT_NAME]);
	}

	if (queryParams[QUERY_PARAM_EPIC] != null) {
		topFilterState.epicIds = queryParamToFilter(queryParams[QUERY_PARAM_EPIC]).map((epicId) =>
			toIssueId(epicId),
		);
	}

	if (queryParams[QUERY_PARAM_ISSUE_TYPE] != null) {
		topFilterState.issueTypeIds = queryParamToFilter(queryParams[QUERY_PARAM_ISSUE_TYPE]).map(
			(issueTypeId) => toIssueTypeId(issueTypeId),
		);
	}

	if (queryParams[QUERY_PARAM_FIX_VERSION] != null) {
		topFilterState.fixVersions = queryParamToFilter(queryParams[QUERY_PARAM_FIX_VERSION]);
	}

	return topFilterState;
};

export const queryParamDateRange = (
	dateFilter: DateFilter,
): {
	startDate: string;
	endDate: string;
} =>
	dateFilter.type === 'RELATIVE'
		? { startDate: dateFilter.startDate, endDate: dateFilter.endDate }
		: {
				startDate: formatDate(dateFilter.startDate, DATE_FORMAT_DASH),
				endDate: formatDate(dateFilter.endDate, DATE_FORMAT_DASH),
			};

export const useQueryParamDateRange = (): [string, string] => {
	const [{ dateFilter }] = useDateFilterStore();
	const { startDate, endDate } = queryParamDateRange(dateFilter);
	return [startDate, endDate];
};

export const filtersToQueryParams = (
	topFilters: TopFilter,
	dateFilter: {
		startDate: string;
		endDate: string;
	},
): QueryParams => ({
	[QUERY_PARAM_TEXT]: topFilters.text || undefined,
	[QUERY_PARAM_ASSIGNEE]: filterToQueryParam(topFilters.assignees),
	[QUERY_PARAM_ENVIRONMENT_NAME]: filterToQueryParam(topFilters.environmentNames),
	[QUERY_PARAM_EPIC]: filterToQueryParam(topFilters.epicIds),
	[QUERY_PARAM_ISSUE_TYPE]: filterToQueryParam(topFilters.issueTypeIds),
	[QUERY_PARAM_FIX_VERSION]: filterToQueryParam(topFilters.fixVersions),
	[QUERY_PARAM_START_DATE]: dateFilter.startDate,
	[QUERY_PARAM_END_DATE]: dateFilter.endDate,
});

export const useQueryParamsFromFilters = (): QueryParams => {
	const [filters] = useTableFilterStore();
	const [startDate, endDate] = useQueryParamDateRange();
	return useMemo(
		() => filtersToQueryParams(filters, { startDate, endDate }),
		[filters, startDate, endDate],
	);
};
